// ** React Imports
import React, { Fragment, useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Stepper from '@mui/material/Stepper'
import MenuItem from '@mui/material/MenuItem'
import Checkbox from '@mui/material/Checkbox'
import ListItemText from '@mui/material/ListItemText'
import StepLabel from '@mui/material/StepLabel'
import Typography from '@mui/material/Typography'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import IconButton from '@mui/material/IconButton'
import CardContent from '@mui/material/CardContent'
import { styled } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import MuiStep from '@mui/material/Step'
import InputAdornment from '@mui/material/InputAdornment'
import IgnoreIcon from '@mui/icons-material/VisibilityOff'
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CardHeader from '@mui/material/CardHeader'
import { DataGrid } from '@mui/x-data-grid'
import { DataGridPro } from '@mui/x-data-grid-pro';
import {TextField, Select, FormControl, InputLabel } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import ListSubheader from '@mui/material/ListSubheader'
import { Dialog,
  DialogTitle,
  DialogContent,
  DialogActions, } from '@mui/material';
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import { ToggleButtonGroup, ToggleButton } from '@mui/material';
import { ToastContainer, toast } from "react-toastify";
// Define the styles for the invalid email cell

// ** Third Party Imports
import axios from 'axios'

// ** Icon Imports
import Icon from '../../../../../components/icon'
import XLSX_Placeholder from "images/Attachment_Icons/XLSX__PlaceholderIcon.png";
import CSV_Placeholder from "images/Attachment_Icons/CSV__PlaceholderIcon.png";

// ** Custom Components Imports
import StepperCustomDot from '../../../../../components/mui/StepperCustomDot'
import CustomTextField from '../../../../../components/mui/text-field'
import CustomChip from '../../../../../components/mui/chip'
import CustomAvatar from '../../../../../components/mui/avatar'
import ServerSideToolbar from '../../../../../components/data-grid/ServerSideToolbar'
// import ServerSideToolbar_WithoutSearch from '../../../../../components/data-grid/ServerSideToolbar_WithoutSearch'

// ** Hook Import
import { useSettings } from '../../../../../components/hooks/useSettings'

// ** Util Import
import { getInitials } from '../../../../../components/utils/get-initials'

// ** Styled Component
import StepperWrapper from '../../../../../components/styles/mui/stepper'

// ** Layout Import
import BlankLayout from '../../../../Layout/blankLayout'
import { GetServices } from 'screens/Requests/logic/GetServices'
import { useServicePermission } from 'screens/Services/permissions/ServicePermissions'
import { SaveService } from 'screens/Services/logic/SaveService'
import { SaveIndustry } from 'screens/Services/logic/SaveIndustry'

    //#region Permissions

const steps = [
  {
    title: 'Map Columns'
  },
  {
    title: 'Fix Errors'
  }
]

const Step = styled(MuiStep)(({ theme }) => ({
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  '&:first-of-type': {
    paddingLeft: 0
  },
  '&:last-of-type': {
    paddingRight: 0
  },
  '& .MuiStepLabel-iconContainer': {
    display: 'none'
  },
  '& .step-subtitle': {
    color: `${theme.palette.text.disabled} !important`
  },
  '& + svg': {
    color: theme.palette.text.disabled
  },
  '&.Mui-completed .step-title': {
    color: theme.palette.text.disabled
  },
  '&.Mui-completed + svg': {
    color: theme.palette.primary.main
  },
  [theme.breakpoints.down('md')]: {
    padding: 0,
    ':not(:last-of-type)': {
      marginBottom: theme.spacing(6)
    }
  }
}))

// ** renders client column
const renderClient = params => {
    const { row } = params
    const stateNum = Math.floor(Math.random() * 6)
    const states = ['success', 'error', 'warning', 'info', 'primary', 'secondary']
    const color = states[stateNum]
  }
  
  const statusObj = {
    1: { title: 'current', color: 'primary' },
    2: { title: 'professional', color: 'success' },
    3: { title: 'rejected', color: 'error' },
    4: { title: 'resigned', color: 'warning' },
    5: { title: 'applied', color: 'info' }
  }
const StepServiceUploadFile = () => {
  
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      navigate('/login', { state: { message: 'Please login to continue.' } }); // Redirect to login if token is not found
    } else {
      setIsLoading(false); // Set loading to false if token is found
    }
  }, [navigate]);

  const location = useLocation();
  const fileData = location.state?.data || []

  const columnNamesFromFile = fileData.length > 0 
  ? Object.keys(fileData[0]).filter(columnName => {

      return fileData.some(row => row[columnName] && row[columnName].toString().trim() !== '');
    }) 
  : [];
    

  const predefinedFields = ['Service Name', 'Industry'];
  const additionalFields = ['Description', 'Flat Rate'];

  const fieldsToRender = [...predefinedFields, ...additionalFields];

  const getDefaultValues = () => {
    const initialValues = {};
    columnNamesFromFile.forEach((columnName) => {
      initialValues[columnName] = fieldsToRender.includes(columnName)
        ? columnName
        : fieldsToRender.some(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase())
          ? fieldsToRender.find(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase())
          : 'select';
    });
    return initialValues;
  };

  const [isMappingConfirmed, setIsMappingConfirmed] = useState(false);
  const [isIgnored, setIsIgnored] = useState(false);

  // ** States
  const [activeStep, setActiveStep] = useState(0)
  const [total, setTotal] = useState(0)
  const [sort, setSort] = useState('asc')
  const [searchValue, setSearchValue] = useState('')
  const [sortColumn, setSortColumn] = useState('first_name')
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 7 })
  const [mappingState, setMappingState] = useState({});
  const [selectedValues, setSelectedValues] = useState(getDefaultValues()); // Track selected dropdown values
  const [errorState, setErrorState] = useState({}); 
  const [myData, setMyData] = useState([]); 
  const [columns, setColumns] = useState([]); 
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(location.state?.message || '');
  const [errorMessages, setErrorMessages] = useState([]);
  const [openErrorListDlg, setOpenErrorListDlg] = useState(false);
  const [openSuccessListDlg, setOpenSuccessListDlg] = useState(false);
  const [successMessages, setSuccessMessages] = useState('');
  const [alertIsDismissed, setAlertIsDismissed] = useState(false);
  const [showConfirmAlert, setShowConfirmAlert] = useState(true);
  const [industries, setIndustries] = useState([]);
  const [displayBanner, setDisplayBanner] = useState(true);

  const { ServicePermissionProps } = useServicePermission();
  const {
      hasServiceSavePermission,
      hasServiceIndustriesGetPermission,
  } = ServicePermissionProps;

  // Add this function to check unmapped mandatory fields
const getUnmappedMandatoryFields = () => {
  const unmappedFields = [];
  
  predefinedFields.forEach(field => {
    // Check if this field is already mapped
    const isMapped = Object.entries(selectedValues).some(([columnName, selectedValue]) => {
      return selectedValue === field && mappingState[columnName]?.confirmed;
    });
    
    if (!isMapped) {
      unmappedFields.push(field);
    }
  });
  
  return unmappedFields;
};

// Update the banner display logic
const unmappedMandatoryFields = getUnmappedMandatoryFields();
const showMandatoryAlert = !alertIsDismissed && unmappedMandatoryFields.length > 0;

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if(showConfirmAlert){
        e.preventDefault(); 
        e.returnValue = ''; 
      }        
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [showConfirmAlert]);



  useEffect(() => {
    let isMounted = true;
    const fetchIndustries = async () => {
      if (!hasServiceIndustriesGetPermission) return;
      try {
        const res = await GetServices(navigate); // or GetServices if truly interchangeable
        if (isMounted && !res.isError) {
          // console.log(res.result)
          // console.log(res)
          setIndustries((res || []).map(i => ({ key: i.industryID, value: i.industryName })));
        }
      } catch (e) {
        console.error('Error fetching industries:', e);
      }
    };
    fetchIndustries();
    return () => { isMounted = false };
  }, [hasServiceIndustriesGetPermission]);
  

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  function loadServerRows(currentPage, data) {
    return data.slice(currentPage * paginationModel.pageSize, (currentPage + 1) * paginationModel.pageSize);
  }

  const fetchTableData = useCallback(
    async (sort, q, column) => {
      let filteredData = [...myData];
  
      if (q) {
        filteredData = filteredData.filter(row =>
          Object.values(row).some(value =>
            value.toString().toLowerCase().includes(q.toLowerCase())
          )
        );
      }
        
      if (sort && column) {
        filteredData = filteredData.sort((a, b) => {
          if (a[column] < b[column]) return sort === 'asc' ? -1 : 1;
          if (a[column] > b[column]) return sort === 'asc' ? 1 : -1;
          return 0;
        });
      }
  
      setTotal(filteredData.length);
      setRows(loadServerRows(paginationModel.page, filteredData));
    },
    [paginationModel.page, paginationModel.pageSize, myData, sort, searchValue, sortColumn]

  );

  useEffect(() => {
    fetchTableData(sort, searchValue, sortColumn)
  }, [fetchTableData, searchValue, sort, sortColumn])

  const handleAlertDismiss = () => {
    setAlertIsDismissed(true);
  }

  const handleSortModel = newModel => {
    if (newModel.length) {
      setSort(newModel[0].sort);
      setSortColumn(newModel[0].field);
      fetchTableData(newModel[0].sort, searchValue, newModel[0].field);
    } else {
      setSort('asc');
      setSortColumn(Object.keys(myData[0])[0]); // Sort by the first key in `mappedData`
    }
  };

  const handleSearch = value => {
    setSearchValue(value);
    fetchTableData(sort, value, sortColumn);
  };

  // const predefinedFields = ['First Name', 'Last Name', 'Email'];
  // const additionalFields = [
  //   'Phone', 'Role', 'Status', 'Date of Birth', 'Referred By', 'Address', 'City', 'State/Province',
  //   'Postal Code', 'Country', 'Emergency Contact Name', 'Emergency Contact Relationship',
  //   'Emergency Contact Phone Number', 'Hire Date', 'Employment Status', 'Departments',
  //   'Job Title', 'Probation End Date', 'Driving license','Driving license expiry', 'Employee Wages Amount',
  //   'Employee Wages Type', 'Availability', 'Clock in / out'
  // ];

  const errorColumns = [
    { flex: 0.25, minWidth: 180, field: 'serviceName', headerName: 'Service Name' },
    { flex: 0.25, minWidth: 180, field: 'failureReason', headerName: 'Reason' }
  ];

  const handleCloseErrorListDlg = () => {
    // sessionStorage.removeItem('myStep');
    // sessionStorage.setItem('myStep', 3);
    setOpenErrorListDlg(false)
    navigate('/onboarding', { state: { step: 6, skipWelcome: true } });
    // navigate('/onboarding');
  }

  const handleCloseSuccessListDlg = () => {
    // sessionStorage.removeItem('myStep');
    // sessionStorage.setItem('myStep', 4);
    setOpenSuccessListDlg(false);
    navigate('/onboarding', { state: { step: 6, skipWelcome: true } });
    // handleNext();
    // navigate('/onboarding');
  }

  const excelDateToJSDate = (excelDate) => {
    const serialDate = Number(excelDate); 
    const excelStartDate = new Date(Date.UTC(1900, 0, 1)); 
    const jsDate = new Date(excelStartDate.getTime() + (serialDate - 1) * 24 * 60 * 60 * 1000); 
  
    if (serialDate > 59) {
      jsDate.setUTCDate(jsDate.getUTCDate() - 1);
    }
  
    return jsDate;
  };
  
  // const formatDate = (date) => {
  //   if (isNaN(date.getTime())) {
  //     return 'Invalid Date';
  //   }
  
  //   const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Get month in UTC
  //   const day = String(date.getUTCDate()).padStart(2, '0'); // Get day in UTC
  //   const year = date.getUTCFullYear(); // Get year in UTC
  
  //   return `${month}-${day}-${year}`; // Format as MM-DD-YYYY
  // };

  const parseDateAsUTC = (dateString) => {
    const [month, day, year] = dateString.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day)); // Creates a UTC date
  };
  
  const formatDate = (date) => {
    const jsDate = typeof date === 'string' ? parseDateAsUTC(date) : date;
    
    if (isNaN(jsDate.getTime())) {
      return 'Invalid Date';
    }
  
    const month = String(jsDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(jsDate.getUTCDate()).padStart(2, '0');
    const year = jsDate.getUTCFullYear();
  
    return `${month}-${day}-${year}`;
  };
  
  // ** Hooks & Var
  const { settings } = useSettings()
  const smallScreen = useMediaQuery(theme => theme.breakpoints.down('md'))
  const { direction } = settings


  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
    // const columnNamesFromFile = fileData.length > 0 
    // ? Object.keys(fileData[0]).filter(columnName => {
    //     // Check if this column has any non-empty value across all rows
    //     return fileData.some(row => row[columnName] && row[columnName].toString().trim() !== '');
    //   }) 
    // : [];
  
    // const fieldsToRender = [...predefinedFields, ...additionalFields];
  
    const updateSelectedValues = () => {
      // Create the new selectedValues object based on mappings
      const newSelectedValues = columnNamesFromFile.reduce((acc, columnName) => {
        if (predefinedFields.includes(columnName)) {
          acc[columnName] = selectedValues[columnName] || columnName;
        }
        return acc;
      }, {});
  
      // Update the state with the new object
      setSelectedValues(newSelectedValues);
    };
  
    useEffect(() => {
      updateSelectedValues();
    }, []);
  
  
    // const handleSelectChange = (columnName) => (event) => {
    //   const newValue = event.target.value;
    
    //   setSelectedValues((prev) => {
  
    //     const updatedValues = { ...prev, [columnName]: newValue };
  
    //     // Re-check for conflicts across all columns
    //     const newErrorState = {};
    //     const valueCounts = {};
    
    //     // Count occurrences of each selected value
    //     Object.values(updatedValues).forEach((value) => {
    //       valueCounts[value] = (valueCounts[value] || 0) + 1;
    //     });
    
    //     // Identify conflicts and update the error state
    //     Object.keys(updatedValues).forEach((key) => {
    //       const value = updatedValues[key];
    //       if (valueCounts[value] > 1 ) {
    //         newErrorState[key] = `${value} has already been matched to another column`;
    //       } 
    //     });
  
    //     setErrorState(newErrorState);
    
    //     return updatedValues;
    //   });
    // };

    const handleSelectChange = (columnName) => (event) => {
      const newValue = event.target.value;
      
      setSelectedValues((prev) => {
        const updatedValues = { ...prev, [columnName]: newValue };
        
        // Re-check for conflicts across all columns
        const newErrorState = {};
        const valueCounts = {};
        
        // Count occurrences of each selected value (EXCLUDING 'select' and empty values)
        Object.values(updatedValues).forEach((value) => {
          if (value && value !== 'select' && value !== '') {
            valueCounts[value] = (valueCounts[value] || 0) + 1;
          }
        });
        
        // Identify conflicts and update the error state
        Object.keys(updatedValues).forEach((key) => {
          const value = updatedValues[key];
          // Only show error for non-select values that have duplicates
          if (value && value !== 'select' && value !== '' && valueCounts[value] > 1) {
            newErrorState[key] = `${value} has already been matched to another column`;
          }
        });
        
        setErrorState(newErrorState);
        
        return updatedValues;
      });
    };
  
    const handleConfirmClick = (columnName) => {
  
      const selectedValue = selectedValues[columnName] || (fieldsToRender.includes(columnName) 
      ? columnName 
      : fieldsToRender.some(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase()) 
        ? fieldsToRender.find(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase()) 
        : 'select');
    
      if (!selectedValue || selectedValue === 'select') {
        setMessage('Please select a valid field before confirming the mapping.');
        return;
      }
    
      setMappingState((prevState) => ({
        ...prevState,
        [columnName]: {
          ...prevState[columnName],
          confirmed: true,
          ignored: false,
          mappedValue: selectedValue || ''
        }
      }));
    };
    
    // const handleIgnoreClick = (columnName) => {
    //   setMappingState((prevState) => ({
    //     ...prevState,
    //     [columnName]: {
    //       confirmed: false,
    //       ignored: true,
    //       mappedValue: '' // Clear the mapped value
    //     }
    //   }));
    // };
    
    const handleEditClick = (columnName) => {
      setMappingState((prevState) => ({
        ...prevState,
        [columnName]: {
          confirmed: false,
          ignored: false,
          mappedValue: '' // Reset the mapping
        }
      }));
    }
  
    // const isConfirmDisabled = (columnName) => {
    //   const selectedValue = selectedValues[columnName] || mappingState[columnName]?.mappedValue || (fieldsToRender.includes(columnName) 
    //   ? columnName 
    //   : fieldsToRender.some(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase()) 
    //     ? fieldsToRender.find(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase()) 
    //     : 'select');
    //   const hasError = Boolean(
    //     selectedValues[columnName] && Object.values(selectedValues).filter(val => val === selectedValue).length > 1
    //   );
    
    //   // Disable if default value is 'select' or if there is an error for the field
    //   return selectedValue === 'select' || hasError;
    // };

    const isConfirmDisabled = (columnName) => {
      const selectedValue = selectedValues[columnName] || mappingState[columnName]?.mappedValue || (fieldsToRender.includes(columnName) 
      ? columnName 
      : fieldsToRender.some(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase()) 
        ? fieldsToRender.find(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase()) 
        : 'select');
      
      // Skip error checking for 'select' values
      if (selectedValue === 'select' || !selectedValue) {
        return true;
      }
      
      const hasError = Boolean(
        selectedValues[columnName] && 
        selectedValues[columnName] !== 'select' &&
        Object.values(selectedValues).filter(val => val === selectedValue && val !== 'select').length > 1
      );
      
      return hasError;
    };

    const handleIgnoreClick = (columnName) => {
      setMappingState((prevState) => ({
        ...prevState,
        [columnName]: {
          confirmed: false,
          ignored: true,
          mappedValue: '' // Clear the mapped value
        }
      }));
    
      // Set to 'select' and recalculate all errors
      setSelectedValues((prev) => {
        const updatedValues = { ...prev, [columnName]: 'select' };
        
        // Re-check for conflicts across all columns
        const newErrorState = {};
        const valueCounts = {};
        
        // Count occurrences of each selected value (excluding 'select' and empty)
        Object.values(updatedValues).forEach((value) => {
          if (value && value !== 'select' && value !== '') {
            valueCounts[value] = (valueCounts[value] || 0) + 1;
          }
        });
        
        // Identify conflicts and update the error state
        Object.keys(updatedValues).forEach((key) => {
          const value = updatedValues[key];
          // Only show error for non-select values that have duplicates
          if (value && value !== 'select' && value !== '' && valueCounts[value] > 1) {
            newErrorState[key] = `${value} has already been matched to another column`;
          }
        });
        
        setErrorState(newErrorState);
        
        return updatedValues;
      });
    };

  // Handle Stepper
  // const handleBack = (val) => {
  //   if (window.confirm("Are you sure you want to go back? Your changes might not be saved.")) {
  //     if (activeStep === 0) {
  //       sessionStorage.removeItem('myStep');
  //       sessionStorage.setItem('myStep', 2);
  //       navigate('/onboarding');
  //     } else {
  //       setActiveStep(prevActiveStep => prevActiveStep - 1)
  //     }
  //   }
  // }
  const handleBack = () => {
    if (!window.confirm("Are you sure you want to go back? Your changes might not be saved.")) return;
    if (activeStep === 0) {
      // BACK → Connect to Stripe (step 2)
      navigate('/onboarding', { state: { step: 2, skipWelcome: true } });
    } else {
      // internal back: Map Columns ↔ Fix Errors
      setActiveStep(s => s - 1);
    }
  };

  // const departmentOptions = departments.map(dep => ({
  //   key: dep.key,
  //   value: dep.value,
  // }));

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        // show only 10 items before scrolling:
        maxHeight: ITEM_HEIGHT * 10 + ITEM_PADDING_TOP, // 48*10 + 8 = 488px
        width: 250,          // you can adjust width as needed
      },
    },
  };

  const buildColumns = useCallback(() => {
      return Object.keys(myData[0] || {}).map(key => {
    if (key === 'Service Name') {
      return {
        field: 'Service Name',
        headerName: 'Service Name',
        flex: 1,
        minWidth: 150,
        editable: true,
        renderEditCell: params => (
          <TextField
            value={params.value || ''}
            onChange={e => {
              const v = e.target.value
              params.api.setEditCellValue({ id: params.id, field: 'Service Name', value: v }, e)
              setMyData(d => d.map(r => r.id === params.id ? { ...r, 'Service Name': v } : r))
              setRows(r => r.map(row => row.id === params.id ? { ...row, 'Service Name': v } : row))
            }}
          />
        ),
        renderCell: params => <Typography>{params.value}</Typography>
      }
    } else if (key === 'Industry') {
      return {
        field: 'Industry',
        headerName: 'Industry',
        flex: 1,
        minWidth: 120,
        editable: true,
        renderEditCell: params => {
          const id = params.id
          return (
            <Select
              value={params.value ?? ''}
              onChange={e => {
                const newKey = e.target.value
                // update grid
                params.api.setEditCellValue({ id, field: 'Industry', value: newKey }, e)
                // update state
                setMyData(d =>
                  d.map(r => (r.id === id ? { ...r, Industry: newKey } : r))
                )
                setRows(r =>
                  r.map(row => (row.id === id ? { ...row, Industry: newKey } : row))
                )
              }}
              MenuProps={MenuProps}  
            >
              {industries.map(ind => (
                <MenuItem key={ind.key} value={ind.key}>
                  {ind.value}
                </MenuItem>
              ))}
              <MenuItem value="">None</MenuItem>
            </Select>
          )
        },
        renderCell: params => {
          const ind = industries.find(i => i.key === params.value)
          const display = ind ? ind.value : 'None'
          const isValid = !!ind
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                backgroundColor: isValid ? 'transparent' : 'rgba(227,0,0,0.1)',
                px: isValid ? 0 : 1,
                py: isValid ? 0 : 0.5,
                borderRadius: 1
              }}
            >
              <Tooltip
                title={
                  isValid
                    ? ''
                    : 'This row will be skipped: invalid industry'
                }
                arrow
              >
                <Typography variant="body2">{display}</Typography>
              </Tooltip>
            </Box>
          )
        }
      }
    } else if (key === 'Description') {
      return {
        field: 'Description',
        headerName: 'Description',
        flex: 1,
        minWidth: 200,
        editable: true,
        renderEditCell: params => (
          <TextField
            value={params.value || ''}
            onChange={e => {
              const v = e.target.value
              params.api.setEditCellValue({ id: params.id, field: 'Description', value: v }, e)
              setMyData(d => d.map(r => r.id === params.id ? { ...r, Description: v } : r))
              setRows(r => r.map(row => row.id === params.id ? { ...row, Description: v } : row))
            }}
          />
        ),
        renderCell: params => <Typography>{params.value}</Typography>
      }
    } else if (key === 'Flat Rate') {
      return {
        field: 'Flat Rate',
        headerName: 'Flat Rate',
        flex: 1,
        minWidth: 120,
        editable: true,
        renderEditCell: params => (
          <TextField
            type="number"
            value={params.value || ''}
            onChange={e => {
              const v = e.target.value
              params.api.setEditCellValue({ id: params.id, field: 'Flat Rate', value: v }, e)
              setMyData(d => d.map(r => r.id === params.id ? { ...r, 'Flat Rate': v } : r))
              setRows(r => r.map(row => row.id === params.id ? { ...row, 'Flat Rate': v } : row))
            }}
          />
        ),
        renderCell: params => <Typography>{params.value || '0'}</Typography>
      }
    } else {
      // leave any other columns untouched
      return {
        field: key,
        headerName: key,
        flex: 1,
        minWidth: 100,
        editable: true,
        renderEditCell: params => <TextField
          value={params.value || ''}
          onChange={e => {
            const v = e.target.value
            params.api.setEditCellValue({ id: params.id, field: key, value: v }, e)
            setMyData(d => d.map(r => r.id === params.id ? { ...r, [key]: v } : r))
            setRows(r => r.map(row => row.id === params.id ? { ...row, [key]: v } : row))
          }}
        />,
        renderCell: params => <Typography>{params.value}</Typography>
      }
    }
  });
  }, [myData, industries]);
  
  useEffect(() => {
    if (myData.length) {
      const filteredColumns = buildColumns().filter(c => c.field !== 'id');
      setColumns(filteredColumns);
    }
  }, [myData, industries, buildColumns])

  // Helper to clean numeric values (remove $, commas, spaces, quotes, etc.)
  const cleanNumericValue = (val) => {
    if (!val) return 0;
    // Convert to string and remove all non-numeric characters except decimal point and minus sign
    const cleaned = val.toString().replace(/[^0-9.-]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  };

const handleNext = async (event) => {

  setMessage('');
// console.log(event.target?.innerText);
// Move to the next step
if(event.target?.innerText === 'Complete Import'){

  const updatedRows = myData.map(row => {
    const updatedRow = { ...row }
    let hasError = false
 
    // 1) Service Name is required
    if (!updatedRow['Service Name'] || !updatedRow['Service Name'].toString().trim()) {
      hasError = true
    }
 
    // 2) Industry must be one of your fetched industries
    if (!industries.some(ind => ind.key === updatedRow['Industry'])) {
      updatedRow['Industry'] = null;
      hasError = true;
    }
 
    // 3) Flat Rate must be a non-negative number (clean it first)
    const rate = cleanNumericValue(updatedRow['Flat Rate'])
    if (rate < 0) {
      // reset negative rates to zero
      updatedRow['Flat Rate'] = 0
    } else {
      updatedRow['Flat Rate'] = rate
    }
 
    // If any required field is bad, skip this row
    if (hasError) return null
    return updatedRow
  })

// Filter out rows that are null (those with email errors)
const filteredRows = updatedRows.filter((row) => row !== null);

// Create the API payload
const payload = {
  rows: filteredRows,
};

const token = localStorage.getItem('jwtToken');

if (!token) {
  // If there's no token, redirect to login page
  setLoading(true);  
  navigate('/login', { state: { message: 'Session expired, please login again.' } });
  return;
}

const apiPayload = payload.rows.map((row) => ({
  serviceOfferingID: 0,
  serviceOfferingName: row['Service Name']?.toString().trim() || null,
  industryID: row['Industry'] || null,
    // find the matching industry.key for this value
    // industries.find(ind => ind.value === row['Industry'])?.key ?? null,
  description: row['Description'] || null,
  flatRate: row['Flat Rate'] != null
    ? cleanNumericValue(row['Flat Rate'])
    : null,
  isActive: true
}));

// console.log("API Payload" + JSON.stringify(apiPayload))

setLoading(true)
setLoading(true)
try {
  const failures = []

  for (let i = 0; i < apiPayload.length; i++) {
    const serviceDto = apiPayload[i]
    let result

    try {
      result = await SaveService(serviceDto, navigate)
    } catch (err) {
      // network / unexpected error
      failures.push({
        id:    i,
        serviceName: serviceDto.serviceOfferingName,
        failureReason: err.message
      })
      continue
    }

    // 1) If the API signals isError
    if (result?.isError) {
      failures.push({
        id:    i,
        serviceName: serviceDto.serviceOfferingName,
        failureReason: result.message || 'Unknown error'
      })
      continue
    }

    // 2) If it returned a 400‐style validation object
    if (result?.errors) {
      // flatten each field’s array of messages into a single string
      const messages = Object.entries(result.errors)
        .flatMap(([field, arr]) => arr.map(msg => `${field}: ${msg}`))
        .join('; ')

      failures.push({
        id:    i,
        serviceName: serviceDto.serviceOfferingName,
        failureReason: messages
      })
    }
  }

  if (failures.length) {
    // show all the ones that failed
    setErrorMessages(failures)
    setOpenErrorListDlg(true)
  } else {
    // nobody failed → success
    setSuccessMessages('Valid services saved successfully.')
    setOpenSuccessListDlg(true)
  }
} catch (err) {
  console.error('Bulk service save error:', err)
  toast.error('An unexpected error occurred during import. Please try again.', {
    position: 'top-right',
    autoClose: 4000,
    pauseOnHover: true,
    draggable: true,
     theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
  })
} finally {
  setLoading(false)
}

} else {
  
  const allMappedOrIgnored = columnNamesFromFile.every(
      (columnName) => mappingState[columnName]?.confirmed || mappingState[columnName]?.ignored
    );
    
    if (!allMappedOrIgnored) {
      setMessage('Please confirm mapping or ignore all columns before continuing.');
      return;
    }

    const requiredFields = ["Service Name", "Industry"];
    const isMappingComplete = requiredFields.every(field =>
      Object.values(mappingState).some(mapping => mapping.mappedValue === field && mapping.confirmed)
    );

    if (!isMappingComplete) {
      setMessage('Please map all the required fields: Service Name, and Industry before proceeding.');
      return;
    }

    // 0) Determine which column is mapped to Industry
    const industryCol = Object.entries(mappingState)
    .find(([col, info]) => info.confirmed && info.mappedValue === 'Industry')?.[0];

    // 1) Gather all distinct incoming industry names (non-empty / not 'None')
    const rawIndustryNames = Array.from(
    new Set(
      fileData
        .map(r => r[industryCol])
        .filter(v => v && v !== 'None')
    )
    );

    // 2) Compare against existing industry values
    const existingNames = industries.map(i => i.value);
    const missingNames = rawIndustryNames.filter(name => !existingNames.includes(name));

    // 3) If any missing, create them then re-fetch
    let lookup = industries;
    if (missingNames.length) {
    setLoading(true);
    for (const name of missingNames) {
      await SaveIndustry({
        industryID: 0,
        industryName: name,
        rate: 0,
        isActive: true,
        companyID: 0
      });
    }
    // Refresh authoritative list
    try {
      const refreshed = await GetServices(navigate);
      if (!refreshed.isError) {
        lookup = (refreshed || []).map(i => ({ key: i.industryID, value: i.industryName }));        
        setIndustries(lookup);
      }
    } finally {
      setLoading(false);
    }
    }

    const mappedData = fileData
    .map((row, i) => {
      const out = { id: i.toString() };
      for (const col in row) {
        if (mappingState[col]?.confirmed) {
          const field = mappingState[col].mappedValue;
          if (field === 'Industry') {
            const text = row[col];
            const match = lookup.find(ind => ind.value === text);
            out['Industry'] = match ? match.key : ''; // keep display value; downstream you resolve to key
          } else {
            out[field] = row[col];
          }
        }
      }
      return out;
    })
    .filter(r => Object.keys(r).length > 1);
  

//     setMyData(mappedData);
  
// // ===========================================================

// const Rows = mappedData.map((row, index) => ({
//   id: index.toString(),
//   ...row,
// }));
  
// setRows(Rows);
// setPaginationModel({ page: 0, pageSize: Rows.length })
setMyData(mappedData);
setRows(mappedData);
setPaginationModel({ page: 0, pageSize: mappedData.length });
// setActiveStep(s => s + 1);

// const columns = 
// ===========================================================

setActiveStep((prevActiveStep) => prevActiveStep + 1);

 }

};

const [alignment, setAlignment] = useState('all')

// helper for phone validity (same rules you already use)
const isValidPhone = (phone = '') => {
  const hasBadChars = phone.match(/[^+\d\s]/)
  if (hasBadChars) return false
  if (phone.startsWith('+') && !phone.startsWith('+1')) return false
  if ((phone.match(/\+/g) || []).length > 1) return false
  const cleaned = phone.replace(/[\+\s]/g, '')
  const norm = cleaned.startsWith('1') ? cleaned : `1${cleaned}`
  if (norm.length !== 11) return false
  if (norm[1] === '1') return false
  return true
}

// build your three buckets
const isValidDate = value => {
  if (value == null || value === '') return false;

  // 1) Excel serial date (a pure number)
  if (typeof value === 'number') {
    const jsDate = excelDateToJSDate(value);
    return !isNaN(jsDate.getTime());
  }

  // 2) “MM/DD/YYYY” or “MM-DD-YYYY”
  //    Normalize to slashes, then split
  const parts = value.replace(/-/g, '/').split('/');
  if (parts.length === 3) {
    const [mm, dd, yyyy] = parts.map(x => parseInt(x, 10));
    if (
      String(mm).length > 0 &&
      String(dd).length > 0 &&
      String(yyyy).length === 4
    ) {
      const d = new Date(yyyy, mm - 1, dd);
      return (
        d.getFullYear() === yyyy &&
        d.getMonth() === mm - 1 &&
        d.getDate() === dd
      );
    }
  }

  // 3) Fallback: let Date.parse handle anything else
  return !isNaN(Date.parse(value));
};

const allRows = rows;

function isRowCompletelyValid(row) {
  // 1) Service Name must be non‐empty
  if (!row['Service Name']?.toString().trim()) return false

  // 2) Industry must match one of the fetched industries
  if (!industries.some(ind => ind.key === row['Industry'])) return false

  // 3) Flat Rate must be a number ≥ 0 (use cleanNumericValue for consistency)
  const rate = cleanNumericValue(row['Flat Rate'])
  if (rate < 0) return false

  // (Description is optional, no extra check)
  return true
}

const validRows   = allRows.filter(isRowCompletelyValid)
const invalidRows = allRows.filter(r => !isRowCompletelyValid(r))

// choose which to show
const displayRows = alignment === 'all'
  ? allRows
  : alignment === 'valid'
    ? validRows
    : invalidRows

const handleAlignment = (event, newAlignment) => {
  if (newAlignment !== null) {
    setAlignment(newAlignment)
  }
}

  const getStepContent = step => {
    switch (step) {
      case 0:
        return (
          <Fragment>
            <Grid container spacing={5} //sx={{mt: 1}}
            >
               {/* {displayBanner && (
                                            <Grid item xs={12}>
                                              <Box
                                                sx={{
                                                  display: "inline-flex",
                                                  justifyContent: "space-between",
                                                  alignItems: "center",
                                                  backgroundColor: "rgb(35, 162, 109, 0.1)",
                                                  border: "1px solid",
                                                  borderColor: "success.main",
                                                  borderRadius: "12px",
                                                  py: 1,
                                                  px: 3,
                                                  gap: 1,
                                                }}
                                              >
                                                <Icon icon="tabler:info-circle" />
                                                <Typography
                                                  variant="h6"
                                                  fontWeight="500"
                                                  sx={{ ml: 2, mr: 3 }}
                                                >
                                                  These fields are editable! -You can use your AI Assistant
                                                  to do this part later.
                                                </Typography>
                                              
                                                  <IconButton onClick={() => setDisplayBanner(false)}>
                                                    <Icon icon="tabler:x" />
                                                  </IconButton>
                                                
                                              </Box>
                                            </Grid>
                                          )} */}
              {/* { showMandatoryAlert && (
                <Grid item xs={12}>
                  <Box sx=
                    {{
                      display: 'inline-flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: 'rgba(255, 159, 67, 0.16)',
                      border: '1px solid', 
                      borderColor: 'warning.main', 
                      borderRadius: '12px', 
                      py: 1,
                      px: 3,
                      gap: 2
                    }}>
                    <Icon icon="tabler:alert-triangle" />
                    <Typography variant='h6' fontWeight='500'>
                      Please ensure {unmappedMandatoryFields.length === 2 
                        ? `the ${unmappedMandatoryFields.join(' and ')} fields are` 
                        : unmappedMandatoryFields.length === 1
                          ? `the ${unmappedMandatoryFields[0]} field is`
                          : 'all fields are'} mapped as {unmappedMandatoryFields.length === 1 ? "it's" : "they are"} required to proceed with the next steps.
                    </Typography>
                    <IconButton onClick={handleAlertDismiss} color='warning'>
                      <Icon icon='tabler:x' />
                    </IconButton>
                  </Box>
                </Grid>
              ) } */}
              { unmappedMandatoryFields.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 2,
                    p: 2,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Icon icon="tabler:info-circle" fontSize={20} />
                      <Typography variant='body1' fontWeight='600' color='primary'>
                        Required Fields to Map ({unmappedMandatoryFields.length})
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap',
                      gap: 1.5,
                      alignItems: 'center'
                    }}>
                      {unmappedMandatoryFields.map((field, index) => (
                        <CustomChip
                          key={index}
                          rounded
                          size="small"
                          skin="light"
                          sx={{
                            lineHeight: 1,
                            paddingTop: "13px",
                            paddingRight: "0 !important",
                            paddingBottom: "13px",
                            borderRadius: "12px !important",
                            fontSize: "medium",
                            border: "1px solid",
                            // m: 0.5
                          }}
                          color="info"
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                pr: 0,
                              }}
                            >
                              {field}

                            </Box>
                          }
                        />
                        
                      ))}
                    </Box>
                  </Box>
                </Grid>
              ) }
              <Grid item xs={12} sm={4}>
                <Typography variant="h5" color='text.secondary' fontWeight='550' gutterBottom>File Source Fields</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
              <Typography variant="h5" color='text.secondary' fontWeight='550' gutterBottom>Cleri Fields</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                {/* Empty grid to align the third column (status buttons) */}
              </Grid>
            
              {/* Dropdowns Row */}
              {columnNamesFromFile.map((columnName, colIndex) => (
                <Fragment key={colIndex}>
                  <Grid item xs={12} sm={4}>
                    <CustomTextField
                      fullWidth
                      label=''
                      value={columnName}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <CustomTextField
                      fullWidth
                      select
                      // defaultValue={
                      //   fieldsToRender.includes(columnName) 
                      //     ? columnName 
                      //     : fieldsToRender.some(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase()) 
                      //       ? fieldsToRender.find(field => field.replace(/\s/g, '').toLowerCase() === columnName.replace(/\s/g, '').toLowerCase()) 
                      //       : 'select'
                      // }
                      value={selectedValues[columnName] || getDefaultValues()[columnName]}
                      label=''
                      onChange={handleSelectChange(columnName)} 
                      error={!!errorState[columnName]} // Apply error if duplicate is detected
                      helperText={errorState[columnName]} // Show helper text if error exists
                      disabled={mappingState[columnName]?.confirmed || mappingState[columnName]?.ignored}
                    >
                      <ListSubheader>Mandatory Fields</ListSubheader>
                      <MenuItem value='select' disabled>Select</MenuItem>
                      {predefinedFields.map((field, index) => (
                        <MenuItem key={index} value={field}>
                          {field}
                        </MenuItem>
                      ))}
                      <ListSubheader>Optional Fields</ListSubheader>
                      {additionalFields.map((field, index) => (
                        <MenuItem key={index} value={field}>
                          {field}
                        </MenuItem>
                      ))}
                    </CustomTextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {mappingState[columnName]?.confirmed ? (
                      <Box sx={{ display: 'flex' }}>
                        <Icon>
                          <CheckCircleIcon sx={{ color: '#1FC16B', height: '17px', width: '17px', my: 'auto', mr: 2 }} />
                        </Icon>
                        <Typography sx={{ fontWeight: 'bold', color: 'success', my: 'auto', mr: 4 }}>Mapping Confirmed</Typography>
                        <Button variant='contained' color='main' onClick={() => handleEditClick(columnName)}>
                          Edit
                        </Button>
                      </Box>
                    ) : mappingState[columnName]?.ignored ? (
                      <Box sx={{ display: 'flex' }}>
                        <Typography sx={{ fontWeight: 'bold', my: 'auto', mr: 4 }}>Ignored</Typography>
                        <Button variant='contained' color='main' onClick={() => handleEditClick(columnName)}>
                          Edit
                        </Button>
                      </Box>
                    ) : (
                      <>
                        <Button
                          variant='contained'
                          sx={{ mr: 4, mb: 2 }}
                          color='main'
                          onClick={() => handleConfirmClick(columnName)}
                          disabled={isConfirmDisabled(columnName)} // Disable based on conditions
                        >
                          Confirm mapping
                        </Button>
                        <Button
                          variant='outlined'
                          color='primary'
                          sx={{ mb: 2 }}
                          onClick={() => handleIgnoreClick(columnName)}
                        >
                          Ignore this column
                        </Button>
                      </>
                    )}
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </Fragment>

        )
      case 1:
        return (
          <Fragment>          
          <Grid item xs={12} sm={12}>
            <ToggleButtonGroup
              exclusive
              value={alignment}
              onChange={handleAlignment}
              aria-label='text alignment'
              sx={{
                backgroundColor: '#F5F7FA',
                borderRadius: 2,
                p: 2,
                '& .MuiToggleButtonGroup-grouped': {
                  margin: '0',
                  border: 'none',
                  borderRadius: 1,
                  '&:not(:first-of-type)': {
                    borderRadius: '4px !important'
                  }
                }
              }}
            >
              <ToggleButton 
                value='all' 
                aria-label='left aligned'
                sx={{
                  px: 6,
                  '&.Mui-selected': {
                    backgroundColor: '#fff',
                    color: 'white !important',
                    '&:hover': {
                      backgroundColor: '#fff !important',
                    }
                  },
                  '&:not(.Mui-selected)': {
                    color: '#6B7280',
                  }
                }}
              >
                <Typography variant='body1' 
                  sx={{ 
                    textTransform: 'lowercase',
                    '&::first-letter': {
                      textTransform: 'uppercase'
                    }
                  }}
                >
                  All {allRows.length}
                </Typography>
              </ToggleButton>
              <ToggleButton 
                value='valid' 
                aria-label='left aligned'
                sx={{
                  px: 6,
                  '&.Mui-selected': {
                    backgroundColor: '#fff',
                    color: 'white !important',
                    '&:hover': {
                      backgroundColor: '#fff !important',
                    }
                  },
                  '&:not(.Mui-selected)': {
                    color: '#6B7280',
                  }
                }}
              >
                <Typography variant='body1' 
                  sx={{ 
                    textTransform: 'lowercase',
                    '&::first-letter': {
                      textTransform: 'uppercase'
                    }
                  }}
                >
                  Valid {validRows.length}
                </Typography>
              </ToggleButton>
              <ToggleButton 
                value='invalid' 
                aria-label='left aligned'
                sx={{
                  px: 6,
                  '&.Mui-selected': {
                    backgroundColor: '#fff',
                    color: 'white !important',
                    '&:hover': {
                      backgroundColor: '#fff !important',
                    }
                  },
                  '&:not(.Mui-selected)': {
                    color: '#6B7280',
                  }
                }}
              >
                <Typography variant='body1' 
                  sx={{ 
                    textTransform: 'lowercase',
                    '&::first-letter': {
                      textTransform: 'uppercase'
                    },
                    color:'#D73838'
                  }}
                >
                  Invalid {invalidRows.length}
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12} sm={12}>
            <DataGridPro
              autoHeight
              pagination
              rows={displayRows}
              rowCount={displayRows.length}
              columns={columns}
              getRowId={(row) => row.id}              
              sortingMode="server"
              paginationMode="server"
              pageSizeOptions={[rows.length, 25, 50]}
              paginationModel={paginationModel}
              onSortModelChange={handleSortModel}
              onPaginationModelChange={setPaginationModel}
              editMode="cell"
              // onCellEditCommit={handleEditCellChangeCommitted}
              slots={{ toolbar: ServerSideToolbar }}
              slotProps={{
                baseButton: {
                  size: 'medium',
                  variant: 'tonal',
                },
                toolbar: {
                  value: searchValue,
                  clearSearch: () => handleSearch(''),
                  onChange: (event) => handleSearch(event.target.value),
                  valueExists: searchValue !== '',
                },
              }}
              sx={{
                '& .MuiDataGrid-columnHeaderTitle': {
                  textTransform: 'none', // This will disable the capitalization
                }
              }}
            />
          </Grid>
        </Fragment>
        )
      default:
        return 'Unknown Step'
    }
  }

  const renderContent = () => {
      return (
        <>
        {message && (
          <Box mb={2} textAlign="center">
            <Typography variant="h6" color={message.includes('successfully') ? 'green' : 'red'}>
              {message.split('\n').map((line, index) => (
                <div key={index}>{line}</div>
              ))}
            </Typography>
          </Box>
        )}
        <form onSubmit={e => e.preventDefault()} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        {getStepContent(activeStep)}
        <Grid item xs={12} sx={{ position: 'fixed', bottom: 0, right: 0, width: '100vw', display: 'flex', justifyContent: 'flex-end', py: 4, pr: 10, backgroundColor: 'white', boxShadow: '0 -2px 5px rgba(0,0,0,0.1)' }}>        
          <Button variant='tonal' color='secondary'  onClick={handleBack} sx={{ mr: 4 }}
          // disabled={activeStep === 0}
          >
            Back
          </Button>              
          <Button variant='contained' color='main' onClick={handleNext}  disabled={loading} >
          {activeStep === steps.length - 1 ? 'Complete Import' : 'Continue'}
          </Button>
        </Grid>
    </form>
    </>
      )
  }

  return (
    <BlankLayout>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '92vh' }}>
          <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <CardHeader
              title={
               <Box sx={{ display: 'flex', flexDirection: smallScreen ? 'column' : 'row', gap: 4 }}>
                  <Box>
                    <img src={XLSX_Placeholder} />
                  </Box>
                  <Box>
                    <Typography variant='h6'>Import data to Services</Typography>
                    <Typography variant='body1' color='text.secondary'>Service data.csv</Typography>
                  </Box>
                </Box>
              }
              action={
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100% !important', py: 3, }}>
                 <StepperWrapper>
                      <Stepper
                        activeStep={activeStep}
                        connector={
                          !smallScreen ? (
                            <Icon icon={'tabler:chevron-right'} />
                          ) : null
                        }
                      >
                        <Step>
                          <StepLabel StepIconComponent={StepperCustomDot}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: 'success.main' }}>
                              <Icon icon='tabler:circle-check-filled' />
                              <Typography color='success.main'>Upload</Typography>
                            </Box>
                          </StepLabel>
                        </Step>
                        {steps.map((step, index) => (
                          <Step key={index}>
                            <StepLabel StepIconComponent={StepperCustomDot}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  color: index >= activeStep ? 'text.primary' : 'success.main'
                                }}
                              >
                                {index < activeStep && <Icon icon='tabler:circle-check-filled' />}
                                <Typography color={index >= activeStep ? 'text.primary' : 'success.main'}>
                                  {step.title}
                                </Typography>
                              </Box>
                            </StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    </StepperWrapper>
                </Box>
              }
            />
            <Divider />
            <CardContent sx={{ flexGrow: 1, overflowY: 'auto' }}>
              {renderContent()}
            </CardContent>
          </Card>
        </Box>

        {/* Error List Dialog */}      
        <Dialog
          fullWidth
          maxWidth="md"
          open={openErrorListDlg}
          onClose={handleCloseErrorListDlg}
          aria-labelledby="form-dialog-title"
          sx={{
            "& .MuiDialog-paper": {
              maxWidth: '650px', // Custom maxWidth between sm and md
              width: '100%', // Ensures it’s responsive within the set max width
            },
          }}
        >
          <DialogTitle>
            <Typography variant="h4" fontWeight='bold'>Save Unsuccessful</Typography>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={5}>
              <Grid item xs={12}>
                <Typography variant="button">The following services will be skipped due to the reasons listed below.</Typography>
              </Grid>
              <Grid item xs={12}>
                <DataGridPro
                  autoHeight              
                  rows={errorMessages}
                  getRowId={(row) => row.id}
                  columns={errorColumns}           
                  sx={{
                    "& .MuiDataGrid-columnHeaderTitle": {
                      textTransform: "none",
                      fontWeight: 'bold'
                    },
                    maxWidth: '600px'
                  }}
                />
              </Grid>
            </Grid>     
          </DialogContent>
          <DialogActions
            sx={{ display: "flex", justifyContent: "flex-end" }}
            className="dialog-actions-dense"
          >
            <Button variant="contained" color="main" onClick={handleCloseErrorListDlg}>
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success List Dialog */}      
        <Dialog
          fullWidth={true}
          maxWidth="xs"
          open={openSuccessListDlg}
          onClose={handleCloseSuccessListDlg}
          aria-labelledby="form-dialog-title"
        >
          <DialogTitle>
            <Typography variant="h4" fontWeight='bold'>Service Import Success</Typography>
          </DialogTitle>
          <DialogContent>
            <Fragment>          
              {successMessages}
            </Fragment>
          </DialogContent>
          <DialogActions
            sx={{ display: "flex", justifyContent: "flex-end" }}
            className="dialog-actions-dense"
          >
            <Button variant="contained" color="main" onClick={handleCloseSuccessListDlg}>
              OK
            </Button>
          </DialogActions>
        </Dialog>
        <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {/*<ToastContainer />*/}
    </BlankLayout>
  )
}

export default StepServiceUploadFile